/**
 * Equipment booking wizard — three progressive steps:
 *   1. When  — pickup/return dates (weekday + holiday + closure guarded)
 *              and a pickup time slot
 *   2. What  — availability table for that window, quantity steppers
 *   3. Confirm — recap + submit through the equipment_create_booking RPC,
 *              which re-validates availability atomically server-side
 *              (the table below is a convenience view, not the authority).
 *
 * Steps 2 and 3 only render once the previous step is valid, mirroring
 * the progressive form the brief sketched. The requester comes from the
 * session and is read-only; an optional "on behalf of" free-text field
 * covers admins booking for a colleague.
 */
import type { JSX } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { state, isDemoMode } from "../state/store";
import { displayName, bySurname, activePeople } from "../utils/names";
import { isWeekend } from "../utils/dates";
import { getHoliday } from "../utils/holidays";
import { restRpc } from "../utils/withRetry";
import { toast } from "../utils/toast";
import {
  computeAvailability,
  availabilityFromAggregates,
  selfRosterName,
  dateProblem,
  timeSlots,
  AVAILABILITY_STATUS_META,
  type AvailabilityAggregate,
  type EquipmentItem,
  type EquipmentBooking,
  type EquipmentBookingLine,
  type ItemAvailability,
} from "../utils/equipment";

interface Props {
  items: EquipmentItem[];
  bookings: EquipmentBooking[];
  lines: EquipmentBookingLine[];
  onCreated: () => void;
}

export function EquipmentWizard({ items, bookings, lines, onCreated }: Props): JSX.Element {
  const s = state.value;
  // The real person (admins' currentUser is the virtual "Administrator").
  const me = selfRosterName(s);

  const [pickupDate, setPickupDate] = useState("");
  const [pickupTime, setPickupTime] = useState("09:30");
  const [returnDate, setReturnDate] = useState("");
  const [returnTime, setReturnTime] = useState("");
  const [purpose, setPurpose] = useState("");
  // Who the kit is FOR — always a roster person, never free text. Users
  // are locked to themselves; admins must pick (defaults to themselves).
  const [forWhom, setForWhom] = useState<string>(me);
  const isAdmin = s.isAdmin;
  const people = useMemo(() => {
    const list = activePeople(s.roster).sort(bySurname);
    if (me && !list.includes(me)) list.unshift(me);
    return list;
  }, [s.roster, me]);
  const [notes, setNotes] = useState("");
  const [qty, setQty] = useState<Record<number, number>>({});
  const [busy, setBusy] = useState(false);
  const [createdRef, setCreatedRef] = useState<string | null>(null);

  const todayIso = new Date().toISOString().slice(0, 10);
  const deps = {
    isWeekend,
    getHolidayName: (iso: string) => getHoliday(iso)?.name ?? null,
    closures: s.closures ?? {},
  };

  const pickupProblem = pickupDate ? dateProblem(pickupDate, todayIso, deps) : null;
  const returnProblem = returnDate
    ? (dateProblem(returnDate, todayIso, deps) ??
      (returnDate < pickupDate ? "Return can't precede pickup" : null))
    : null;
  // Step 1 is complete when the window is valid AND a person is chosen.
  const windowValid =
    !!pickupDate && !!returnDate && !pickupProblem && !returnProblem && !!pickupTime && !!forWhom;

  // Availability for the chosen window. Real mode asks the
  // equipment_availability RPC — per-item aggregates computed over ALL
  // bookings server-side, so non-admins (who can only read their own
  // rows) still see correct numbers without seeing colleagues' bookings.
  // Demo mode computes locally from the synthetic register. Either way
  // the create RPC re-validates on submit, so a stale table can never
  // oversubscribe.
  const [availability, setAvailability] = useState<ItemAvailability[]>([]);
  const [availLoading, setAvailLoading] = useState(false);
  useEffect(() => {
    if (!windowValid) {
      setAvailability([]);
      return;
    }
    if (isDemoMode.value) {
      setAvailability(computeAvailability(items, bookings, lines, pickupDate, returnDate));
      return;
    }
    let stale = false;
    setAvailLoading(true);
    restRpc<AvailabilityAggregate>("equipment_availability", {
      p_from: pickupDate,
      p_to: returnDate,
    })
      .then((rows) => {
        if (!stale) setAvailability(availabilityFromAggregates(items, rows));
      })
      .catch((err) => {
        if (!stale) {
          setAvailability([]);
          toast(err instanceof Error ? err.message : "Couldn't load availability");
        }
      })
      .finally(() => {
        if (!stale) setAvailLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [windowValid, items, bookings, lines, pickupDate, returnDate]);
  const freeByItem = useMemo(() => {
    const m = new Map<number, number>();
    availability.forEach((a) => m.set(a.item.id, a.free));
    return m;
  }, [availability]);

  const selection = Object.entries(qty)
    .map(([id, n]) => ({ item_id: Number(id), qty: n }))
    .filter((l) => l.qty > 0);
  const selectionValid =
    selection.length > 0 &&
    selection.every((l) => l.qty <= (freeByItem.get(l.item_id) ?? 0));

  // Non-admins always book in their own name regardless of what the
  // (disabled) picker holds; the create RPC enforces the same rule
  // server-side (migration 24). The booker is the signed-in person; if
  // they have no roster identity (an unlinked admin, the demo account)
  // the chosen person IS the requester. on_behalf_of is stored only when
  // the kit is for someone other than the booker.
  const effectiveFor = isAdmin ? forWhom : me || forWhom;
  const requesterName = me || effectiveFor;
  const onBehalfOf = effectiveFor && effectiveFor !== requesterName ? effectiveFor : "";

  const setItemQty = (itemId: number, next: number): void => {
    const max = freeByItem.get(itemId) ?? 0;
    setQty((q) => ({ ...q, [itemId]: Math.max(0, Math.min(max, next)) }));
  };

  const submit = async (): Promise<void> => {
    if (!windowValid || !selectionValid || busy) return;
    if (!forWhom || !requesterName) {
      toast("Choose who the equipment is for.");
      return;
    }
    setBusy(true);
    try {
      const rows = await restRpc<{ booking_id: number; reference: string }>(
        "equipment_create_booking",
        {
          p_requester_name: requesterName,
          p_on_behalf_of: onBehalfOf,
          p_pickup_date: pickupDate,
          p_pickup_time: pickupTime,
          p_return_date: returnDate,
          p_return_time: returnTime,
          p_purpose: purpose.trim(),
          p_notes: notes.trim(),
          p_lines: selection,
        },
      );
      const ref = rows[0]?.reference ?? "";
      setCreatedRef(ref || "created");
      toast(`Booking submitted${ref ? ` — ${ref}` : ""}`);
      onCreated();
    } catch (err) {
      // The RPC raises human-readable errors (e.g. which item is short).
      toast(err instanceof Error ? err.message : "Could not create the booking");
    } finally {
      setBusy(false);
    }
  };

  const reset = (): void => {
    setCreatedRef(null);
    setQty({});
    setPurpose("");
    setNotes("");
    setForWhom(me);
  };

  if (createdRef) {
    return (
      <div class="eq-card eq-done">
        <div class="eq-done-icon" aria-hidden="true">✓</div>
        <h3>Request submitted</h3>
        <p>
          Reference <b>{createdRef}</b> · status <b>Pending</b>. An admin will
          review it; you can follow it (and cancel it) under <b>My requests</b>.
        </p>
        <button type="button" class="modal-primary" onClick={reset}>
          New booking
        </button>
      </div>
    );
  }

  return (
    <div class="eq-wizard">
      {/* ── Step 1 — When ── */}
      <div class="eq-card">
        <h3 class="eq-step-title"><span class="eq-step-n">1</span> When</h3>
        <div class="eq-form-grid">
          <label class="eq-field">
            <span>For whom</span>
            <select
              value={forWhom}
              disabled={!isAdmin}
              required
              onChange={(e) => setForWhom((e.target as HTMLSelectElement).value)}
              title={isAdmin ? "Pick the person the equipment is for (yourself by default)" : "Equipment is booked in your own name"}
            >
              {!forWhom && <option value="">— choose a person —</option>}
              {people.map((p) => <option key={p} value={p}>{displayName(p)}</option>)}
            </select>
            {me && forWhom && forWhom !== me && (
              <em class="eq-field-hint">Booked by you ({displayName(me)}) for {displayName(forWhom)}</em>
            )}
          </label>
          <label class="eq-field">
            <span>Pickup date</span>
            <input
              type="date"
              value={pickupDate}
              min={todayIso}
              onInput={(e) => setPickupDate((e.target as HTMLInputElement).value)}
            />
            {pickupProblem && <em class="eq-field-err">{pickupProblem}</em>}
          </label>
          <label class="eq-field">
            <span>Pickup time</span>
            <select value={pickupTime} onChange={(e) => setPickupTime((e.target as HTMLSelectElement).value)}>
              {timeSlots().map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label class="eq-field">
            <span>Expected return date</span>
            <input
              type="date"
              value={returnDate}
              min={pickupDate || todayIso}
              onInput={(e) => setReturnDate((e.target as HTMLInputElement).value)}
            />
            {returnProblem && <em class="eq-field-err">{returnProblem}</em>}
          </label>
          <label class="eq-field">
            <span>Return time <i>(optional)</i></span>
            <select value={returnTime} onChange={(e) => setReturnTime((e.target as HTMLSelectElement).value)}>
              <option value="">—</option>
              {timeSlots().map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label class="eq-field eq-field-wide">
            <span>Purpose / project <i>(optional)</i></span>
            <input
              type="text"
              value={purpose}
              onInput={(e) => setPurpose((e.target as HTMLInputElement).value)}
              placeholder="e.g. GREENFUTURE final event — video interviews"
              maxLength={120}
            />
          </label>
        </div>
      </div>

      {/* ── Step 2 — What ── */}
      {windowValid && (
        <div class="eq-card">
          <h3 class="eq-step-title"><span class="eq-step-n">2</span> What</h3>
          {availLoading && <div class="eq-empty">Checking availability…</div>}
          <div class="eq-scroll">
            <table class="eq-table" aria-label="Equipment availability for the requested window">
              <thead>
                <tr>
                  <th>Item</th>
                  <th class="num">Total</th>
                  <th class="num">Free</th>
                  <th>Status</th>
                  <th class="num">Quantity</th>
                </tr>
              </thead>
              <tbody>
                {availability
                  .slice()
                  .sort((x, y) => x.item.name.localeCompare(y.item.name))
                  .map((a) => {
                          const meta = AVAILABILITY_STATUS_META[a.status];
                          const selectable = a.free > 0;
                          const current = qty[a.item.id] ?? 0;
                          const why =
                            a.status === "fuori_servizio"
                              ? "Flagged out of service / lost"
                              : a.free === 0
                                ? `All units reserved or out for ${pickupDate} → ${returnDate}`
                                : `${a.free} of ${a.item.total_qty} free for this window`;
                          return (
                            <tr key={a.item.id}>
                              <td>
                                <b>{a.item.name}</b>
                                {a.item.unit_notes && <span class="eq-unit-notes">{a.item.unit_notes}</span>}
                              </td>
                              <td class="num">{a.item.total_qty}</td>
                              <td class="num"><b>{a.free}</b></td>
                              <td>
                                <span class={`eq-chip ${meta.cls}`} title={why}>
                                  <span aria-hidden="true">{meta.icon}</span> {meta.label}
                                </span>
                              </td>
                              <td class="num">
                                <span class="eq-stepper" role="group" aria-label={`Quantity for ${a.item.name}`}>
                                  <button
                                    type="button"
                                    disabled={!selectable || current === 0}
                                    onClick={() => setItemQty(a.item.id, current - 1)}
                                    aria-label={`Remove one ${a.item.name}`}
                                  >
                                    −
                                  </button>
                                  <span aria-live="polite">{current}</span>
                                  <button
                                    type="button"
                                    disabled={!selectable || current >= a.free}
                                    onClick={() => setItemQty(a.item.id, current + 1)}
                                    aria-label={`Add one ${a.item.name}`}
                                    title={selectable ? undefined : why}
                                  >
                                    +
                                  </button>
                                </span>
                              </td>
                            </tr>
                          );
                        })}
              </tbody>
            </table>
          </div>
          <div class="eq-selection-summary" aria-live="polite">
            {selection.length === 0
              ? "Nothing selected yet — pick at least one item."
              : selection
                  .map((l) => {
                    const it = items.find((i) => i.id === l.item_id);
                    return `${l.qty}× ${it?.name ?? "?"}`;
                  })
                  .join(" · ")}
          </div>
        </div>
      )}

      {/* ── Step 3 — Confirm ── */}
      {windowValid && selectionValid && (
        <div class="eq-card">
          <h3 class="eq-step-title"><span class="eq-step-n">3</span> Confirm</h3>
          <dl class="eq-recap">
            <div><dt>For</dt><dd>{displayName(forWhom)}{onBehalfOf && ` — booked by ${displayName(requesterName)}`}</dd></div>
            <div><dt>Pickup</dt><dd>{pickupDate} · {pickupTime}</dd></div>
            <div><dt>Expected return</dt><dd>{returnDate}{returnTime && ` · ${returnTime}`}</dd></div>
            {purpose.trim() && <div><dt>Purpose</dt><dd>{purpose.trim()}</dd></div>}
            <div>
              <dt>Items</dt>
              <dd>
                <ul class="eq-recap-items">
                  {selection.map((l) => {
                    const it = items.find((i) => i.id === l.item_id);
                    return <li key={l.item_id}>{l.qty}× {it?.name ?? "?"}</li>;
                  })}
                </ul>
              </dd>
            </div>
          </dl>
          <label class="eq-field eq-field-wide">
            <span>Notes for the admin <i>(optional)</i></span>
            <textarea
              value={notes}
              onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)}
              maxLength={400}
              placeholder="Anything the admin should know"
            />
          </label>
          <div class="modal-actions">
            <button type="button" class="modal-primary" disabled={busy} onClick={() => void submit()}>
              {busy ? "Submitting…" : "Submit request"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
